import { z } from 'zod';

/**
 * Integrations platform — Connector descriptor SSOT (пак integrations, B1–B3).
 *
 * Провайдер интеграции = плагин по SPI. `descriptor` драйвит каталог (B2),
 * wizard (F1) и UI-формы по `auth_kind`. Один и тот же Zod — backend registry
 * и frontend catalog. Новый провайдер = новый descriptor, без правок ядра.
 */

/** Тип аутентификации провайдера — драйвит динамическую форму wizard (F1/B3). */
export const AuthKind = z.enum([
  'webhook_url', // Bitrix24 incoming webhook URL (содержит user+token)
  'api_token', // Adesk / Финград: единый api_token
  'oauth2', // OAuth2 redirect flow (заглушка)
  'odata_basic', // 1С OData: base_url + login + password
]);
export type AuthKind = z.infer<typeof AuthKind>;

/**
 * Возможность провайдера — какие ресурсы он умеет тянуть. Драйвит F4 («что тянем»)
 * и capability-scope enforcement (B12). FE локализует enum-code.
 */
export const ConnectorCapability = z.enum([
  'counterparties',
  'operations',
  'items',
  'warehouses',
  'boms',
  'batches',
]);
export type ConnectorCapability = z.infer<typeof ConnectorCapability>;

/**
 * Identity priority при cross-source merge (B9): меньший tier = выше приоритет,
 * высший не перезаписывается низшим. 1С=1 (регуляторный L1), Bitrix=2, Adesk=4.
 */
export const ConnectorTier = z.number().int().min(1).max(5);
export type ConnectorTier = z.infer<typeof ConnectorTier>;

/** Пресеты расписания синхронизации (F5/B11). */
export const SchedulePreset = z.enum(['manual', 'hourly', 'daily', 'custom']);
export type SchedulePreset = z.infer<typeof SchedulePreset>;

/**
 * ConnectorDescriptor — статический паспорт провайдера. Возвращается каталогом
 * (B2) без секретов. `available=false` → карточка «скоро» (1С/Финград стабы B16).
 */
export const ConnectorDescriptor = z.object({
  key: z.string().min(1).max(40), // 'bitrix' | 'adesk' | '1c' | 'fingrad'
  name: z.string().min(1).max(80), // бренд (не локализуется)
  auth_kind: AuthKind,
  capabilities: z.array(ConnectorCapability).min(1),
  multi_entity: z.boolean(), // один токен = N юрлиц (Adesk 4)
  tier: ConnectorTier,
  default_schedule: SchedulePreset,
  available: z.boolean(), // false → «скоро» (descriptor-стаб)
});
export type ConnectorDescriptor = z.infer<typeof ConnectorDescriptor>;

/** B2 — GET /api/v1/integrations/catalog. */
export const CatalogResponse = z.object({
  items: z.array(ConnectorDescriptor),
});
export type CatalogResponse = z.infer<typeof CatalogResponse>;

// ─── Auth credential schemas (per auth_kind) — B3 ─────────────────────────
// Каждый kind — собственная strict cred-схема. Wizard рендерит поля по kind.

export const WebhookUrlCreds = z
  .object({
    webhook_url: z.string().url(),
  })
  // zod-strict-ok: cred-валидация (validateCreds safeParse)
  .strict();
export type WebhookUrlCreds = z.infer<typeof WebhookUrlCreds>;

export const ApiTokenCreds = z
  .object({
    api_token: z.string().min(1),
  })
  // zod-strict-ok: cred-валидация (validateCreds safeParse)
  .strict();
export type ApiTokenCreds = z.infer<typeof ApiTokenCreds>;

export const OdataBasicCreds = z
  .object({
    base_url: z.string().url(),
    login: z.string().min(1),
    password: z.string().min(1),
  })
  // zod-strict-ok: cred-валидация (validateCreds safeParse)
  .strict();
export type OdataBasicCreds = z.infer<typeof OdataBasicCreds>;

export const OAuth2Creds = z
  .object({
    client_id: z.string().min(1),
    client_secret: z.string().min(1),
    redirect_uri: z.string().url().optional(),
  })
  // zod-strict-ok: cred-валидация (validateCreds safeParse)
  .strict();
export type OAuth2Creds = z.infer<typeof OAuth2Creds>;

/** Дискриминированная по auth_kind схема кредов (для wizard submit). */
export const ConnectorCreds = z.union([
  WebhookUrlCreds,
  ApiTokenCreds,
  OdataBasicCreds,
  OAuth2Creds,
]);
export type ConnectorCreds = z.infer<typeof ConnectorCreds>;

/** Какие поля рендерит wizard для каждого auth_kind (FE-driven из descriptor). */
export const AUTH_KIND_FIELDS: Record<AuthKind, readonly string[]> = {
  webhook_url: ['webhook_url'],
  api_token: ['api_token'],
  odata_basic: ['base_url', 'login', 'password'],
  oauth2: ['client_id', 'client_secret', 'redirect_uri'],
};
